/**
 * Service API pour les prédictions solaires - SolarPredict
 * 
 * Ce module est le cœur de l'application puisqu'il gère toutes les requêtes
 * liées à la prédiction de production des modules photovoltaïques:
 * - Upload des données de capteurs (fichiers CSV)
 * - Exécution des prédictions via les modèles d'IA
 * - Sauvegarde et gestion des résultats de prédiction
 * 
 * J'ai standardisé ce service API en utilisant la même structure que
 * les autres services (auth.js, module.js).
 */

import axios from 'axios';

// Configuration de l'URL de l'API - Utilisation du port 4000 pour le backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Instance Axios avec configuration personnalisée
const api = axios.create({
  baseURL: API_URL + '/api',  // Construction de l'URL complète (http://localhost:4000/api)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Upload sensor data file
 */
export const uploadSensorData = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await api.post('/predictions/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Upload failed');
  }
};

/**
 * Set prediction configuration (string and model selection)
 */
export const setPredictionConfig = async (stringId, modelId) => {
  try {
    const response = await api.post('/predictions/config', {
      stringId,
      modelId
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to set configuration');
  }
};

/**
 * Get current prediction configuration
 */
export const getPredictionConfig = async () => {
  try {
    const response = await api.get('/predictions/config');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get configuration');
  }
};

/**
 * Run prediction with current configuration
 */
export const runPrediction = async () => {
  try {
    const response = await api.post('/predictions/predict');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Prediction failed');
  }
};

/**
 * Get prediction results
 */
export const getPredictionResults = async () => {
  try {
    const response = await api.get('/predictions/results');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get results');
  }
};

/**
 * Get specific prediction result
 */
export const getPredictionResult = async (id) => {
  try {
    const response = await api.get(`/predictions/results/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get result');
  }
};

/**
 * Save prediction result
 */
export const savePredictionResult = async (resultData) => {
  try {
    const response = await api.post('/predictions/save-result', resultData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to save result');
  }
};

/**
 * Delete prediction result
 */
export const deletePredictionResult = async (id) => {
  try {
    const response = await api.delete(`/predictions/results/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete result');
  }
};

/**
 * Service pour récupérer l'historique des prédictions
 * @returns {Promise<Object>} - Historique des prédictions
 */
export const getPredictionHistory = async () => {
  try {
    const response = await api.get('/predictions/history');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get history');
  }
};

// Simple function to update chosen string
export const updateChosenString = async (stringId) => {
  try {
    const response = await axios.post(`http://localhost:4000/api/update-string`, 
      { stringId },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating string:', error);
    throw error;
  }
};

/**
 * Get all predictions for the current user
 */
export const getAllUserPredictions = async () => {
  try {
    const response = await api.get('/predictions/user-predictions');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get user predictions');
  }
};

/**
 * Get chart data for a specific prediction
 */
export const getPredictionChartData = async (predictionId) => {
  try {
    const response = await api.get(`/predictions/chart-data/${predictionId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get prediction chart data');
  }
};

export default api;
